import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { fetchWrapper } from '../../utils/apiHandlers'
import { useShopLocation, milesLabel } from '../../utils/shopLocation'
import LoadingDotsComp from '../LoadingDotsComp'
import { Photo, Rating } from './shopcommon'
import theme from '../../theme'

export interface Message {
  from: 'bot' | 'user'
  text: string
  businesses?: any[]
  chatid?: string
  note?: string
  options?: string[]   // quick replies when the assistant asks a clarifying question
}

interface Prop {
  // Scope the chat to one business (used on a business page).
  slug?: string
  category?: string
  greeting?: string
  placeholder?: string
  suggestions?: string[]
  // Ask this question on the visitor's behalf (e.g. they tapped a trending topic). `n` makes repeats fire again.
  inject?: { text: string; n: number } | null
  // Saved conversations (the main Ask Amigo chat on /ask): the server stores the chat and supplies the history.
  persist?: boolean
  chatid?: string
  initialMessages?: Message[]
  onSaved?: (cid: string) => void
}

const DEFAULT_GREETINGS = [
  "Hi! What's on your mind? Tell me what you need, or just how your day is going, and I'll find local places that can help.",
  "Hey there! What's going on? Ask for anything, or tell me what you're dealing with, and I'll point you to someone nearby.",
  "Hello! What can I help you with today? A craving, a gift, a problem - whatever it is, let's find something local.",
  "Hi! Anything on your mind? Share it and I'll suggest local businesses that might help.",
]

const DEFAULT_PLACEHOLDERS = [
  "What's on your mind?",
  "Tell me what's going on...",
  "What do you need today?",
  "Ask me anything...",
  "How can I help?",
]

const pick = (items: string[]) => items[Math.floor(Math.random() * items.length)]

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Business names in the answer become links to the business itself (the same place the cards go).
const linkify = (text: string, businesses: any[], track: (b: any) => void) => {
  if (!businesses || !businesses.length) return text
  const names: { name: string; b: any }[] = []
  businesses.forEach((b) => {
    names.push({ name: b.name, b })
    const short = String(b.name).split(' - ')[0].trim()
    if (short.length >= 3 && short !== b.name) names.push({ name: short, b })
  })
  names.sort((a, b) => b.name.length - a.name.length)
  const re = new RegExp(`(${names.map((n) => escapeRegExp(n.name)).join('|')})`, 'gi')
  return text.split(re).map((part, i) => {
    const hit = names.find((n) => n.name.toLowerCase() === part.toLowerCase())
    return hit ? (
      <InlineLink key={i} href={hit.b.link} target="_blank" rel="noopener noreferrer" onClick={() => track(hit.b)}>
        {part}
      </InlineLink>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  })
}

const ShopChat: React.FC<Prop> = ({ slug, category, greeting, placeholder, suggestions = [], inject, persist, chatid, initialMessages, onSaved }) => {
  // Picked once per mount so the wording varies between visits but doesn't flicker while chatting.
  const [greetingtext] = useState(() => greeting || pick(DEFAULT_GREETINGS))
  const [placeholdertext] = useState(() => placeholder || pick(DEFAULT_PLACEHOLDERS))
  const [messages, setmessages] = useState<Message[]>(initialMessages && initialMessages.length ? initialMessages : [{ from: 'bot', text: greetingtext }])
  const [cid, setcid] = useState(chatid || '')
  const [question, setquestion] = useState('')
  const [waiting, setwaiting] = useState(false)
  const chatRef = useRef<HTMLDivElement>(null)
  const { location } = useShopLocation()

  useEffect(() => {
    // Scroll the chat box itself, not the page, so opening /ask doesn't jump the viewport.
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight
  }, [messages, waiting])

  const ask = async (text?: string) => {
    const q = (text ?? question).trim()
    if (!q || waiting) return

    // The last two exchanges give the assistant enough context for follow-ups ("what time do they close?").
    const history: { q: string; a: string }[] = []
    for (let i = 1; i < messages.length - 1; i++) {
      if (messages[i].from === 'user' && messages[i + 1].from === 'bot') history.push({ q: messages[i].text, a: messages[i + 1].text })
    }

    setmessages((m) => [...m, { from: 'user', text: q }])
    setquestion('')
    setwaiting(true)
    try {
      const response: any = await fetchWrapper.post('/api/directory/chat', {
        question: q, slug, category, history: history.slice(-5), persist: !!persist, chatid: cid,
        lat: location ? location.lat : undefined,
        lon: location ? location.lon : undefined,
        locationcity: location ? location.city : undefined,
        locationstate: location ? location.state : undefined,
      })
      if (response.chatid) {
        onSaved && onSaved(response.chatid)   // every saved message: the list re-sorts, a claimed guest chat appears
        setcid(response.chatid)
      }
      const first = (response.businesses || [])[0]
      const note = response.outside && first
        ? `Nothing like that near ${response.location ? response.location.city : 'you'} yet, so here's what's available in ${first.city}.`
        : ''
      setmessages((m) => [...m, { from: 'bot', text: response.answer, businesses: response.businesses, chatid: response.chatid, note, options: response.options }])
    } catch (error) {
      setmessages((m) => [...m, { from: 'bot', text: 'Something went wrong, please try again in a moment.' }])
    }
    setwaiting(false)
  }

  useEffect(() => {
    if (inject && inject.text) ask(inject.text)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inject && inject.n])

  const track = (chatid: string | undefined, b: any) => {
    // Fire-and-forget: the link itself opens the business's own site in a new tab.
    fetchWrapper.post('/api/directory/click', { slug: b.slug, url: b.link, chatid }).catch(() => {})
  }

  return (
    <Card>
      <ChatWindow ref={chatRef}>
        {messages.map((m, i) => (
          <React.Fragment key={i}>
            <Bubble $fromUser={m.from === 'user'}>
              {m.from === 'bot' && m.businesses && !slug ? linkify(m.text, m.businesses, (b) => track(m.chatid, b)) : m.text}
            </Bubble>
            {m.note && <Note>{m.note}</Note>}
            {m.options && m.options.length > 0 && i === messages.length - 1 && (
              <Suggestions style={{ padding: 0 }}>
                {m.options.map((o) => (
                  <Chip key={o} onClick={() => ask(`I'm looking for ${o.toLowerCase()}`)}>{o}</Chip>
                ))}
              </Suggestions>
            )}
            {!slug && m.businesses && m.businesses.length > 0 && (
              <Results>
                {m.businesses.map((b) => (
                  <Suggestion key={b.slug} href={b.link} target="_blank" rel="noopener noreferrer" onClick={() => track(m.chatid, b)}>
                    <Photo src={b.image} fallback={b.images && b.images[0]} name={b.name} height={112} />
                    <SuggestionBody>
                      <SName>{b.name}</SName>
                      <SMeta>
                        {[b.categorylabel, b.distancekm !== undefined ? `${milesLabel(b.distancekm)} away` : b.city].filter(Boolean).join(' · ')}
                      </SMeta>
                      <Rating rating={b.rating} count={b.ratingcount} />
                      <Visit>Visit website ↗</Visit>
                    </SuggestionBody>
                  </Suggestion>
                ))}
              </Results>
            )}
          </React.Fragment>
        ))}
        {waiting && (
          <Bubble $fromUser={false}>
            <LoadingDotsComp />
          </Bubble>
        )}
      </ChatWindow>

      {suggestions.length > 0 && messages.length === 1 && (
        <Suggestions>
          {suggestions.map((s) => (
            <Chip key={s} onClick={() => ask(s)}>{s}</Chip>
          ))}
        </Suggestions>
      )}

      <InputRow>
        <Input
          placeholder={placeholdertext}
          value={question}
          maxLength={250}
          onChange={(e) => setquestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask() }}
        />
        <SendButton onClick={() => ask()} disabled={waiting || !question.trim()}>
          Ask
        </SendButton>
      </InputRow>
    </Card>
  )
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.raised};
  overflow: hidden;
`

const ChatWindow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  height: 460px;
  overflow-y: auto;
`

const Bubble = styled.div<{ $fromUser: boolean }>`
  align-self: ${(p) => (p.$fromUser ? 'flex-end' : 'flex-start')};
  background: ${(p) => (p.$fromUser ? theme.colors.primary : theme.colors.primarySoft)};
  color: ${(p) => (p.$fromUser ? '#fff' : theme.colors.text)};
  padding: 10px 14px;
  border-radius: ${(p) => (p.$fromUser ? '14px 14px 2px 14px' : '14px 14px 14px 2px')};
  max-width: 88%;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
`

const InlineLink = styled.a`
  color: ${theme.colors.primaryDark};
  font-weight: 700;
  text-decoration: underline;
  &:hover {
    color: ${theme.colors.primary};
  }
`

const Note = styled.div`
  align-self: flex-start;
  font-size: 12px;
  color: ${theme.colors.textMuted};
  font-style: italic;
`

const Results = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

const Suggestion = styled.a`
  width: 200px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  background: ${theme.colors.surface};
  transition: box-shadow 0.15s, border-color 0.15s;
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadow.card};
  }
`

const SuggestionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 9px 11px 11px;
`

const SName = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: ${theme.colors.primaryDark};
`

const SMeta = styled.span`
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

const Visit = styled.span`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  color: ${theme.colors.primary};
`

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 20px 12px;
`

const Chip = styled.button`
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.textMuted};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid ${theme.colors.border};
`

const Input = styled.input`
  flex: 1;
  padding: 10px 14px;
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.border};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${theme.colors.primary};
  }
`

const SendButton = styled.button`
  padding: 10px 20px;
  border-radius: ${theme.radius.md};
  border: none;
  background: ${theme.colors.primary};
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

export default ShopChat
